import React, { useState, useEffect } from 'react';
import { UserPlus, Check, X, Mail, User, Loader2, Clock } from 'lucide-react';
import { useFriendStore } from '../store/friendStore';
import { useInviteStore } from '../store/inviteStore';
import { format } from 'date-fns';

export function FriendsList() {
  const {
    friends,
    pendingRequests,
    sentRequests,
    loading: friendsLoading,
    error: friendsError,
    fetchFriends,
    fetchPendingRequests,
    fetchSentRequests,
    addFriend,
    acceptFriend,
    rejectFriend
  } = useFriendStore();

  const {
    sentInvites,
    receivedInvites,
    loading: invitesLoading,
    error: invitesError,
    fetchSentInvites,
    fetchReceivedInvites
  } = useInviteStore();

  const [newFriendEmail, setNewFriendEmail] = useState('');
  const [addingFriend, setAddingFriend] = useState(false);

  useEffect(() => {
    // Load all friend-related data
    Promise.all([
      fetchFriends(),
      fetchPendingRequests(),
      fetchSentRequests(),
      fetchSentInvites(),
      fetchReceivedInvites()
    ]);
  }, [fetchFriends, fetchPendingRequests, fetchSentRequests, fetchSentInvites, fetchReceivedInvites]);

  const handleAddFriend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFriendEmail.trim()) return;

    setAddingFriend(true);
    try {
      await addFriend(newFriendEmail);
      setNewFriendEmail('');
    } finally {
      setAddingFriend(false);
    }
  };

  const loading = friendsLoading || invitesLoading;
  const error = friendsError || invitesError;

  return (
    <div className="space-y-6">
      {/* Add Friend Form */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-4">Add Friend</h2>
        <form onSubmit={handleAddFriend} className="space-y-4">
          <div>
            <label htmlFor="friend-email" className="block text-sm font-medium text-gray-700 mb-1">
              Friend's Email
            </label>
            <div className="flex gap-2">
              <input
                id="friend-email"
                type="email"
                value={newFriendEmail}
                onChange={(e) => setNewFriendEmail(e.target.value)}
                placeholder="Enter your friend's email"
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                disabled={addingFriend || !newFriendEmail.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {addingFriend ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <UserPlus className="w-5 h-5" />
                )}
                Add Friend
              </button>
            </div>
          </div>
        </form>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      {/* Received Invites */}
      {receivedInvites.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Received Invites</h2>
          <div className="space-y-3">
            {receivedInvites.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-gray-500" />
                  </div>
                  <div>
                    <div className="font-medium">{invite.inviter_email}</div>
                    <div className="text-sm text-gray-500">
                      Invited you {format(new Date(invite.created_at), 'MMM d, yyyy')}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => acceptFriend(invite.inviter_id)}
                    className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg"
                    title="Accept invite"
                  >
                    <Check className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => rejectFriend(invite.inviter_id)}
                    className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                    title="Reject invite"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Friend Requests (Received) */}
      {pendingRequests.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Friend Requests</h2>
          <div className="space-y-3">
            {pendingRequests.map((friend) => (
              <div
                key={friend.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-gray-500" />
                  </div>
                  <div>
                    <div className="font-medium">{friend.email}</div>
                    <div className="text-sm text-gray-500">Wants to be friends</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => acceptFriend(friend.id)}
                    className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg"
                    title="Accept request"
                  >
                    <Check className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => rejectFriend(friend.id)}
                    className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                    title="Reject request"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sent Friend Requests */}
      {sentRequests.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Sent Friend Requests</h2>
          <div className="space-y-3">
            {sentRequests.map((friend) => (
              <div
                key={friend.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-gray-500" />
                  </div>
                  <div>
                    <div className="font-medium">{friend.email}</div>
                    <div className="text-sm text-gray-500">Request sent</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sent Invites (to non-users) */}
      {sentInvites.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Pending Email Invites</h2>
          <div className="space-y-3">
            {sentInvites.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-gray-500" />
                  </div>
                  <div>
                    <div className="font-medium">{invite.email}</div>
                    <div className="text-sm text-gray-500">
                      Invited {format(new Date(invite.created_at), 'MMM d, yyyy')}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Friends List */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-4">Friends</h2>
        {loading && friends.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            Loading friends...
          </div>
        ) : friends.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <User className="w-8 h-8 mx-auto mb-2" />
            No friends yet. Add some friends to get started!
          </div>
        ) : (
          <div className="space-y-3">
            {friends.map((friend) => (
              <div
                key={friend.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-gray-500" />
                  </div>
                  <div className="font-medium">{friend.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}